import { useEffect } from "react";
import type { SEOSection, OpenGraphImage } from "@/types/sections/seo";

function setOrUpdateMeta(nameOrProp: { name?: string; property?: string }, content: string | null | undefined) {
  if (typeof document === "undefined") return;
  const selector = nameOrProp.name
    ? `meta[name="${nameOrProp.name}"][data-seo-managed="true"]`
    : `meta[property="${nameOrProp.property}"][data-seo-managed="true"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!content) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    existing.setAttribute("content", content);
    return;
  }
  const meta = document.createElement("meta");
  if (nameOrProp.name) meta.setAttribute("name", nameOrProp.name);
  if (nameOrProp.property) meta.setAttribute("property", nameOrProp.property);
  meta.setAttribute("content", content);
  meta.setAttribute("data-seo-managed", "true");
  document.head.appendChild(meta);
}

function setOrUpdateLink(rel: string, href: string | null | undefined, extra: Record<string, string> = {}) {
  if (typeof document === "undefined") return;
  // Build a proper compound selector like: link[rel="canonical"][data-seo-managed="true"][hreflang="en"]
  const parts: string[] = [`[rel="${rel}"]`, `[data-seo-managed="true"]`];
  if (extra.hreflang) parts.push(`[hreflang="${extra.hreflang}"]`);
  const selector = `link${parts.join("")}`;
  const existing = document.head.querySelector(selector) as HTMLLinkElement | null;

  // If no href => remove existing managed link
  if (!href) {
    if (existing) existing.remove();
    return;
  }

  // Update existing
  if (existing) {
    existing.setAttribute("href", href);
    Object.entries(extra).forEach(([k, v]) => existing.setAttribute(k, v));
    return;
  }

  // Create new
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v));
  link.setAttribute("data-seo-managed", "true");
  document.head.appendChild(link);
}

function removeAllManaged(selector: string) {
  if (typeof document === "undefined") return;
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
}

function setTitle(title?: string | null, template?: string | null) {
  if (typeof document === "undefined") return;
  if (!title) return;
  const finalTitle = template ? template.replace("%s", title) : title;
  document.title = finalTitle;
}

function setJSONLD(structuredData?: object | string | null) {
  if (typeof document === "undefined") return;
  const id = "seo-structured-data";
  const existing = document.getElementById(id);
  if (!structuredData) {
    if (existing) existing.remove();
    return;
  }
  const script = (existing as HTMLScriptElement | null) ?? document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.setAttribute("data-seo-managed", "true");
  try {
    script.textContent = typeof structuredData === "string" ? structuredData : JSON.stringify(structuredData);
  } catch {
    // ignore malformed JSON
    script.textContent = "";
  }
  if (!existing) document.head.appendChild(script);
}

export interface SEOHeadProps {
  seo?: SEOSection | null;
  language?: string | null;
}

export function SEOHead({ seo, language }: SEOHeadProps) {
  useEffect(() => {
    // Clean hreflang and pagination links before re-applying
    removeAllManaged('link[data-seo-managed="true"][rel="alternate"]');
    removeAllManaged('link[data-seo-managed="true"][rel="prev"]');
    removeAllManaged('link[data-seo-managed="true"][rel="next"]');

    if (!seo) return;

    // Title & Description
    setTitle(seo.title, seo.titleTemplate ?? null);
    setOrUpdateMeta({ name: "description" }, seo.metaDescription);

    // Canonical
    setOrUpdateLink("canonical", seo.canonicalUrl ?? undefined);

    // Robots
    if (seo.robots) {
      const rules: string[] = [];
      rules.push(seo.robots.index === false ? "noindex" : "index");
      rules.push(seo.robots.follow === false ? "nofollow" : "follow");
      if (seo.robots.noarchive) rules.push("noarchive");
      if (seo.robots.nosnippet) rules.push("nosnippet");
      if (typeof seo.robots.maxSnippet === "number") rules.push(`max-snippet:${seo.robots.maxSnippet}`);
      if (seo.robots.maxImagePreview) rules.push(`max-image-preview:${seo.robots.maxImagePreview}`);
      if (typeof seo.robots.maxVideoPreview === "number") rules.push(`max-video-preview:${seo.robots.maxVideoPreview}`);
      setOrUpdateMeta({ name: "robots" }, rules.join(","));
    }

    // Open Graph
    const og = seo.openGraph ?? {};
    // Prefer flattened fields, fall back to nested for backwards compatibility
    const ogImages = seo.ogImages ?? og.images ?? [];
    const ogLocaleAlternates = seo.ogLocaleAlternates ?? og.localeAlternates ?? [];

    setOrUpdateMeta({ property: "og:title" }, og.title ?? seo.title ?? undefined);
    setOrUpdateMeta({ property: "og:description" }, og.description ?? seo.metaDescription ?? undefined);
    setOrUpdateMeta({ property: "og:type" }, og.type ?? undefined);
    setOrUpdateMeta({ property: "og:url" }, og.url ?? seo.canonicalUrl ?? undefined);
    setOrUpdateMeta({ property: "og:site_name" }, og.site_name ?? undefined);
    setOrUpdateMeta({ property: "og:locale" }, og.locale ?? (language ? `${language}_${language.toUpperCase()}` : undefined));
    // OG locale alternates
    ogLocaleAlternates.forEach((locale: string) => {
      if (locale) setOrUpdateMeta({ property: "og:locale:alternate" }, locale);
    });

    // Single image fallback
    if (og.image) setOrUpdateMeta({ property: "og:image" }, og.image);
    // Multi-image (from flattened ogImages or nested og.images)
    ogImages.forEach((img: OpenGraphImage, idx: number) => {
      setOrUpdateMeta({ property: idx === 0 ? "og:image" : `og:image:${idx}` }, img.url);
      if (img.width) setOrUpdateMeta({ property: `og:image:width` }, String(img.width));
      if (img.height) setOrUpdateMeta({ property: `og:image:height` }, String(img.height));
      if (img.alt) setOrUpdateMeta({ property: `og:image:alt` }, img.alt);
      if (img.type) setOrUpdateMeta({ property: `og:image:type` }, img.type);
    });

    // Twitter
    const tw = seo.twitter ?? {};
    setOrUpdateMeta({ name: "twitter:card" }, tw.card ?? (og.image || ogImages[0] ? "summary_large_image" : "summary"));
    setOrUpdateMeta({ name: "twitter:title" }, tw.title ?? seo.title ?? undefined);
    setOrUpdateMeta({ name: "twitter:description" }, tw.description ?? seo.metaDescription ?? undefined);
    setOrUpdateMeta({ name: "twitter:image" }, tw.image ?? og.image ?? ogImages[0]?.url ?? undefined);
    if (tw.site) setOrUpdateMeta({ name: "twitter:site" }, tw.site);
    if (tw.creator) setOrUpdateMeta({ name: "twitter:creator" }, tw.creator);

    // OG image fallback from Twitter image if none provided in OG
    if (!og.image && ogImages.length === 0 && tw.image) {
      setOrUpdateMeta({ property: "og:image" }, tw.image);
    }

    // Hreflang alternates
    (seo.alternateLanguages ?? []).forEach((alt) => {
      if (!alt || !alt.href || !alt.hreflang) return;
      setOrUpdateLink("alternate", alt.href, { hreflang: alt.hreflang });
    });

    // Pagination
    if (seo.pagination?.relPrev) setOrUpdateLink("prev", seo.pagination.relPrev);
    if (seo.pagination?.relNext) setOrUpdateLink("next", seo.pagination.relNext);

    // Site verification
    const verify = seo.siteVerification ?? {};
    if (verify.google) setOrUpdateMeta({ name: "google-site-verification" }, verify.google);
    if (verify.bing) setOrUpdateMeta({ name: "msvalidate.01" }, verify.bing);
    if (verify.pinterest) setOrUpdateMeta({ name: "p:domain_verify" }, verify.pinterest);
    if (verify.yandex) setOrUpdateMeta({ name: "yandex-verification" }, verify.yandex);
    if (verify.facebook) setOrUpdateMeta({ name: "facebook-domain-verification" }, verify.facebook);

    // JSON-LD: use provided structuredData, or synthesize a minimal WebSite schema as fallback
    const fallbackJSONLD = !seo.structuredData
      ? {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: seo.siteName ?? seo.title ?? undefined,
          url: seo.canonicalUrl ?? undefined,
          inLanguage: language ?? undefined,
          sameAs: seo.sameAs ?? undefined,
        }
      : null;
    setJSONLD(seo.structuredData ?? fallbackJSONLD ?? null);

    return () => {
      // Optional cleanup: do not wipe title to avoid flash; remove managed alternates/pagination/script to keep DOM clean
      removeAllManaged('link[data-seo-managed="true"][rel="alternate"]');
      removeAllManaged('link[data-seo-managed="true"][rel="prev"]');
      removeAllManaged('link[data-seo-managed="true"][rel="next"]');
      const json = document.getElementById("seo-structured-data");
      if (json) json.remove();
    };
  }, [seo, language]);

  return null;
}

export default SEOHead;
